/* eslint-disable jsx-a11y/anchor-is-valid */
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap-v5';
import { useDispatch } from 'react-redux';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios, { AxiosError } from 'axios';
import clsx from 'clsx';
import { FormikHelpers, useFormik } from 'formik';
import { KTSVG } from 'src/_metronic/helpers';
import { withPimURL } from 'src/helpers/service';
import { PLATFORM } from 'src/services/axios/ExpireAxios';
import { announcementUnRead } from 'src/services/redux/slices/announcement/announcement-slice';
import { _login } from 'src/services/redux/slices/auth/auth-slice';
import * as Yup from 'yup';
import { LOGIN_URL, login } from '../redux/AuthCRUD';
import { LockOTP } from './LockOTP';
import { AuthOTP, IOtpScreenData } from './OTP';

interface IFormData {
  email: string;
  password: string;
}

const loginSchema = Yup.object().shape({
  email: Yup.string().email('Geçersiz email adresi').required('Lütfen email adresinizi girin.'),
  password: Yup.string().min(4, 'Şifreniz en az 4 karakter olmalıdır.').required('Lütfen şifrenizi girin.'),
});

const initialValues: IFormData = {
  email: '',
  password: '',
};

export function Login() {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const [otpScreenData, setOtpScreenData] = useState<null | IOtpScreenData>(null);
  const [otpLockScreenData, setOtpLockScreenData] = useState<{ phoneNumber?: string; userName: string } | null>(null);

  const loginMutation = useMutation({
    mutationKey: ['app-login-mutation'],
    mutationFn: (data: IFormData) => {
      const { email, password } = data;
      return login(email, password);
    },
    onSuccess: ({ data }: any) => {
      if (!!data?.requiredChangePassword) {
        history.push({
          pathname: '/auth/change-password',
          state: { email: data?.user?.email },
        });
        return;
      }
      if (data?.smsValidation) {
        setOtpScreenData(data);
      } else if (data?.user?.vendor?.id) {
        const searchParams = new URLSearchParams(location.search);
        const redirect = searchParams.get('redirect');
        dispatch(_login(data));
        dispatch(announcementUnRead());
        if (redirect && redirect !== 'logout') history.push(`/${redirect}`);
      } else {
        toast.error('Bir sorun oluştu.');
      }
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        switch (error?.response?.data?.message || error?.response?.data?.error) {
          case 'INVALID_PASSWORD':
            toast.error('Email veya şifrenizi kontrol edin!');
            break;
          case 'SMS_CODE_IS_NOT_EXPIRED':
            toast.error('3 dakika içinde 1 kez doğrulama kodu talep edebilirsiniz.');
            break;
          case 'ACCOUNT_LOCKED_TOMANY_RETRY':
            toast.error('Bu hesapta birden fazla deneme yapıldığı için kilitlendi.');
            setOtpLockScreenData({
              userName: formik.values.email,
              phoneNumber: error?.response?.data?.error?.phone || error?.response?.data?.phone,
            });
            break;
          case 'ACCOUNT_LOCKED_TO_MANY_RETRY':
            toast.error('Bu hesapta birden fazla deneme yapıldığı için kilitlendi.');
            setOtpLockScreenData({
              userName: formik.values.email,
              phoneNumber: error?.response?.data?.error?.phone || error?.response?.data?.phone,
            });
            break;
          case 'VENDOR_ACCESS_DENIED_CHECK_PERMISSION':
            toast.error('Satıcı ekranına erişim izniniz bulunmamaktadır.');
            break;
          case 'VENDOR_IS_NOT_ACTIVE':
            toast.error('Satıcı aktif değil');
            break;
          case 'TOO_MANY_ATTEMPTS_ON_SMS_CODE_VALIDATION':
            toast.error('Doğrulama kodu deneme sayısını aştınız!');
            break;
          case 'SMS_TOKEN_NOT_ACTIVE':
            toast.error('Onay kodunun süresi doldu. Lütfen tekrar onay kodu isteyiniz.');
            break;
          case 'SMS_VALIDATION_BLOCKED_A_DAY':
            toast.error('1 saat içerisinde yalnızca 10 kez doğrulama kodu talep edebilirsiniz.');
            break;
          case 'WRONG_SMS_CODE':
            toast.error('Girdiğiniz kod eksik veya hatalı. Kontrol edip tekrar deneyin.');
            break;
          case 'SMS_TOKEN_NOT_CONFIRMED':
            toast.error('Onay kodu geçersiz');
            break;
          case 'VENDOR_PHONE_NOT_FOUND':
            toast.error('Satıcının telefon numarası bulunamadı.');
            break;
          default:
            toast.error('Bir sorun oluştu.');
            break;
        }
      } else {
        toast.error('Bir sorun oluştu.');
      }
    },
    retry: false,
    cacheTime: 0,
  });

  const loading = loginMutation.isLoading;

  const onSubmit = (values: IFormData, { setSubmitting }: FormikHelpers<IFormData>) => {
    loginMutation.mutate(values);
    setSubmitting(false);
  };

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit,
  });

  if (otpLockScreenData)
    return (
      <LockOTP
        data={otpLockScreenData}
        onBack={() => {
          setOtpLockScreenData(null);
          formik.setFieldValue('password', '');
          formik.setFieldTouched('password', false);
          formik.setSubmitting(false);
        }}
      />
    );

  const showOtpScreen = !!otpScreenData?.token?.length;
  if (showOtpScreen)
    return (
      <AuthOTP
        data={otpScreenData}
        setOtpLockScreenData={setOtpLockScreenData}
        otpLockScreenData={{ userName: formik.values.email }}
        onBack={() => {
          setOtpScreenData(null);
          formik.setFieldValue('password', '');
          formik.setFieldTouched('password', false);
          formik.setSubmitting(false);
        }}
      />
    );

  return (
    <form className="form w-100" onSubmit={formik.handleSubmit} noValidate id="kt_login_signin_form">
      {/* begin::Heading */}
      <div className="text-center mb-10">
        <h1 className="text-dark mb-3">IDEFIX'de Oturum Açın</h1>
      </div>
      {/* begin::Heading */}
      <div className="fv-row mb-10">
        <label className="form-label fs-6 fw-bolder text-dark">Email</label>
        <div className="position-relative">
          <input
            placeholder="Email"
            {...formik.getFieldProps('email')}
            className={clsx('form-control form-control-lg form-control-solid')}
            type="email"
            name="email"
            autoComplete="off"
          />
          {formik.touched.email && formik.errors.email && (
            <OverlayTrigger placement="top" overlay={<Tooltip id="email">{formik.errors.email}</Tooltip>}>
              <div className="position-absolute translate-middle-y top-50 end-0 me-3 bg-transparent">
                <KTSVG path="/media/icons/duotone/General/Info.svg" className="svg-icon-2 text-danger svg-icon-gray-500 " />
              </div>
            </OverlayTrigger>
          )}
        </div>
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className="fv-row mb-10">
        <div className="d-flex justify-content-between mt-n5">
          <div className="d-flex flex-stack mb-2 justify-content-between w-100">
            <label className="form-label fw-bolder text-dark fs-6 mb-0">Şifre</label>
            <Link to="/auth/forgot-password" className="link-primary fs-6 fw-bolder ">
              Şifrenizi mi unuttunuz ?
            </Link>
          </div>
        </div>
        <div className="position-relative">
          <input type="password" autoComplete="off" {...formik.getFieldProps('password')} className={clsx('form-control form-control-lg form-control-solid')} />
          {formik.touched.password && formik.errors.password && (
            <OverlayTrigger placement="top" overlay={<Tooltip id="password">{formik.errors.password}</Tooltip>}>
              <div className="position-absolute translate-middle-y top-50 end-0 me-3 bg-transparent">
                <KTSVG path="/media/icons/duotone/General/Info.svg" className="svg-icon-2 text-danger svg-icon-gray-500 " />
              </div>
            </OverlayTrigger>
          )}
        </div>
      </div>
      {/* end::Form group */}

      {/* begin::Action */}
      <div className="text-center">
        <button type="submit" id="kt_sign_in_submit" className="btn btn-lg btn-primary w-100 mb-5" disabled={!formik.isValid}>
          {!loading && <span className="indicator-label">Devam et</span>}
          {loading && (
            <span className="indicator-progress" style={{ display: 'block' }}>
              Lütfen bekleyin...
              <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
            </span>
          )}
        </button>
      </div>
      {/* end::Action */}
    </form>
  );
}
